import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Award, Users, MapPin, Home, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

export const LeadAnalytics = ({ onLocalityClick }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/analytics/leads", { params: { days: 7 } }).then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center py-10 text-[var(--ink-muted)]">Loading…</div>;

  const Stat = ({ icon: Icon, label, value, hint, color }) => (
    <div className="p-5 rounded-2xl bg-white border border-[var(--line)]">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--ink-muted)] font-semibold">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="font-serif text-3xl text-[var(--teak)] mt-2">{value}</div>
      {hint && <div className="text-xs text-[var(--ink-muted)] mt-1">{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Leads · 7d" value={data.total_leads} color="var(--primary)" />
        <Stat icon={Award} label="Gold+ Buyers" value={data.high_score_leads} hint="high-intent" color="var(--gold)" />
        <Stat icon={MapPin} label="Top Locality" value={data.top_localities[0]?.label || "—"} hint={data.top_localities[0] ? `${data.top_localities[0].count} leads` : ""} color="var(--emerald)" />
        <Stat icon={Wallet} label="Hot Price Band" value={data.top_price_bands[0]?.label || "—"} hint={data.top_price_bands[0] ? `${data.top_price_bands[0].count} leads` : ""} color="var(--teak)" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="p-5 rounded-2xl bg-white border border-[var(--line)]">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-semibold mb-3">Leads over last {data.period_days} days</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.by_day} margin={{ left: -24 }}>
              <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#5C5652" />
              <YAxis fontSize={11} stroke="#5C5652" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#C85A32" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-5 rounded-2xl bg-white border border-[var(--line)]">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-semibold mb-3">BHK demand</div>
          <div className="space-y-2">
            {data.top_bhk.length === 0 && <div className="text-sm text-[var(--ink-muted)] italic">No data yet.</div>}
            {data.top_bhk.map((row) => {
              const max = Math.max(...data.top_bhk.map((r) => r.count));
              const w = max ? (row.count / max) * 100 : 0;
              return (
                <div key={row.label} className="flex items-center gap-3">
                  <div className="w-16 text-sm text-[var(--teak)]"><Home className="w-3.5 h-3.5 inline mr-1" />{row.label}</div>
                  <div className="flex-1 h-3 bg-[var(--surface)] rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--gold)]" style={{ width: `${w}%` }} />
                  </div>
                  <div className="w-10 text-right text-sm font-mono text-[var(--primary)]">{row.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-white border border-[var(--line)]" data-testid="top-localities-chart">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-semibold">Top localities</div>
          <div className="text-[11px] text-[var(--ink-muted)]">Tap a bar to filter inquiries</div>
        </div>
        {data.top_localities.length === 0 ? (
          <div className="text-sm text-[var(--ink-muted)] italic py-8 text-center">Add listings to see trends.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(180, data.top_localities.length * 44)}>
            <BarChart
              data={data.top_localities}
              layout="vertical"
              margin={{ top: 4, right: 24, bottom: 4, left: 24 }}
            >
              <XAxis type="number" fontSize={11} stroke="#5C5652" allowDecimals={false} />
              <YAxis type="category" dataKey="label" fontSize={12} stroke="#2B1E1A" width={120} />
              <Tooltip
                cursor={{ fill: "rgba(200,90,50,0.08)" }}
                formatter={(v) => [`${v} leads`, "Count"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #E0DBD0", fontSize: 12 }}
              />
              <Bar
                dataKey="count"
                radius={[0, 6, 6, 0]}
                cursor="pointer"
                onClick={(entry) => onLocalityClick && onLocalityClick(entry?.label)}
              >
                {data.top_localities.map((loc, i) => (
                  <Cell
                    key={loc.label}
                    fill={i === 0 ? "#C85A32" : i === 1 ? "#D47547" : "#E5A478"}
                    data-testid={`locality-bar-${loc.label}`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
